import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Card,
  Col, Container, Row,
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalculator } from '@fortawesome/free-solid-svg-icons';
import meta from '../../../../meta';
import { EditorControls, editorHooks } from '../../../basicEditor/editorControls';
import { Footer, DocumentBody } from '../../../../components/Containers/docs';
import useEditor from '../../../newEditor/hook/editor';
import { DocCommandPanel } from '../../../newEditor/commandPanels';
import EditorContainer from '../../../newEditor/editorContainer';
import useEditorParams from '../../../newEditor/hook/params';
import HidableContainer from '../../../documents/common/hidableContainer';
import Equpmanet from './tp/equipment';
import CalcArea from '../../../documents/aktDvk/editor/calcArea';

const { backendURL } = meta.cat.objectexamination;

function Editor({
  onSave, onClose, id,
}) {
  const editorParams = useEditorParams();
  const {
    data, actions, fields, fieldErrors, nonFieldErrors, options, isNew, changed, permissions,
    loading, systemErrors, headerReadOnlyFields, printForms, headerProps,
  } = useEditor({
    backendURL,
    id,
    onCloseCallBack: onClose,
    onSaveCallBack: onSave,
    ...editorParams,
  });

  const [keyParam, setKeyParam] = useState('');

  const informationProps = editorHooks.useTextInputProps('information', data, fields, fieldErrors, actions.onChange, false, headerReadOnlyFields);
  const nameProps = editorHooks.useStringInputProps('name', data, fields, fieldErrors, actions.onChange, false, headerReadOnlyFields);
  const place = editorHooks.useStringInputProps('place', data, fields, fieldErrors, actions.onChange, false, headerReadOnlyFields);
  const noteProps = editorHooks.useStringInputProps('note', data, fields, fieldErrors, actions.onChange, false, headerReadOnlyFields);

  const areaConnectTubeProps = editorHooks.useNumberInputProps('area_connect_tube', data, fields, fieldErrors, actions.onChange, false, headerReadOnlyFields);
  const areaSmoke1 = editorHooks.useStringInputProps('area_smoke1', data, fields, fieldErrors, actions.onChange, false, headerReadOnlyFields);
  const areaSmoke2 = editorHooks.useStringInputProps('area_smoke2', data, fields, fieldErrors, actions.onChange, false, headerReadOnlyFields);
  const areaVent1 = editorHooks.useStringInputProps('area_vent1', data, fields, fieldErrors, actions.onChange, false, headerReadOnlyFields);
  const areaVent2 = editorHooks.useStringInputProps('area_vent2', data, fields, fieldErrors, actions.onChange, false, headerReadOnlyFields);
  const countChannelsProps = editorHooks.useNumberInputProps('count_channels', data, fields, fieldErrors, actions.onChange, false, headerReadOnlyFields);
  const lengthSmokeTubeProps = editorHooks.useNumberInputProps('length_smoke_tube', data, fields, fieldErrors, actions.onChange, false, headerReadOnlyFields);
  const turnsSmokeTube = editorHooks.useSelectorInputProps('turns_smoke_tube', data, fields, fieldErrors, actions.onChange, false, headerReadOnlyFields);

  const connectTube = editorHooks.useSelectorInputProps('connect_tube', data, fields, fieldErrors, actions.onChange, false, headerReadOnlyFields);
  const smokeChannel = editorHooks.useSelectorInputProps('smoke_channel', data, fields, fieldErrors, actions.onChange, false, headerReadOnlyFields);
  const ventChannel = editorHooks.useSelectorInputProps('vent_channel', data, fields, fieldErrors, actions.onChange, false, headerReadOnlyFields);

  const houseProps = editorHooks.useItemInputProps('house', data, fields, fieldErrors, actions.onChange, false, headerReadOnlyFields);
  const contragent = editorHooks.useItemInputProps('contragent', data, fields, fieldErrors, actions.onChange, false, headerReadOnlyFields);
  const ownerContragent = editorHooks.useItemInputProps('owner_contragent', data, fields, fieldErrors, actions.onChange, false, headerReadOnlyFields);
  const localityProps = editorHooks.useItemInputProps('locality', data, fields, fieldErrors, actions.onChange, false, headerReadOnlyFields);
  const localityAreaProps = editorHooks.useItemInputProps('locality_area', data, fields, fieldErrors, actions.onChange, false, headerReadOnlyFields);
  const streetProps = editorHooks.useItemInputProps('street', data, fields, fieldErrors, actions.onChange, false, headerReadOnlyFields);

  const setAddr = useCallback((newData) => {
    const locality = newData.locality ? newData.locality.name : '';
    const localityArea = newData.locality_area ? `, ${newData.locality_area.name}` : '';
    const street = newData.street ? `, ${newData.street.name}` : '';
    const house = newData.house ? `, ${newData.house.number}` : '';

    actions.onChange({
      ...newData,
      address: `${locality}${localityArea}${street}${house}`,
    });
  }, [actions]);

  const setArea = useCallback((area, key) => {
    actions.onChange({
      [key]: area,
    });
  }, [actions]);

  const [paramVent, setParamVent] = useState({
    area_vent1: {
      isDim: false,
      isDiam: false,
      diam: 0,
      x: 0,
      y: 0,
      note: areaVent1.value,
    },
    area_vent2: {
      isDim: false,
      isDiam: false,
      diam: 0,
      x: 0,
      y: 0,
      note: areaVent2.value,
    },
    area_smoke1: {
      isDim: false,
      isDiam: false,
      diam: 0,
      x: 0,
      y: 0,
      note: areaSmoke1.value,
    },
    area_smoke2: {
      isDim: false,
      isDiam: false,
      diam: 0,
      x: 0,
      y: 0,
      note: areaSmoke2.value,
    },
  });

  const streetFilter = useMemo(() => {
    const filter = { locality: data.locality?.id };
    if (data.locality_area) {
      return {
        ...filter,
        area: data.locality_area?.id,
      };
    }

    return filter;
  }, [data.locality, data.locality_area]);

  return (
    <EditorContainer
      isNew={isNew}
      name={options.name}
      repr={data.repr}
      isLoading={loading}
      err={systemErrors}
      nonFieldErrors={nonFieldErrors}
      onClearNonFieldErrors={actions.onClearNonFieldErrors}
      onClearErrors={actions.onClearErrs}
      onClose={actions.onClose}
      changed={changed}
      fields={fields}
      CommandPanel={(
        <DocCommandPanel
          backendURL={backendURL}
          permissions={permissions}
          actions={actions}
          changed={changed}
          executed={data.executed}
          printForms={printForms}
          id={id}
          repr={data.repr}
        />
      )}
    >
      <DocumentBody>
        <HidableContainer>
          <Container fluid style={{ padding: '0' }}>

            <Card className="border-0 p-3 m-3">
              <Card.Title>
                <h6 className="fs-2">{data.address}</h6>
              </Card.Title>

              <Card.Body className="p-1">
                <Row>
                  <Col>
                    <EditorControls.StringInput {...nameProps} />
                  </Col>

                  <Col lg={3}>
                    <EditorControls.ItemPicker {...contragent} />
                  </Col>
                  <Col lg={3}>
                    <EditorControls.ItemPicker
                      {...ownerContragent}
                      filter={{
                        contragent: data?.contragent?.id,
                      }}
                    />
                  </Col>
                  <Col lg={2}>
                    <EditorControls.NumberInput {...countChannelsProps} />
                  </Col>
                </Row>
              </Card.Body>

            </Card>
            <Card className="border-0 p-3 m-3">
              <Card.Title>
                Адреса та місце складання
              </Card.Title>
              <Card.Subtitle>
                {data.address}
              </Card.Subtitle>
              <Card.Body className="p-1">
                <Row>
                  <Col lg={3}>
                    <EditorControls.ItemPicker
                      {...localityProps}
                      onChange={(e, v) => {
                        actions.onChange({ locality: v });
                        setAddr({
                          ...data,
                          locality: v,
                        });
                      }}
                    />
                  </Col>
                  <Col lg={3}>
                    <EditorControls.ItemPicker
                      {...localityAreaProps}
                      onChange={(e, v) => {
                        actions.onChange({
                          locality_area: v,
                        });
                        setAddr({
                          ...data,
                          locality_area: v,
                        });
                      }}
                      filter={{ locality: data.locality?.id }}
                    />
                  </Col>
                  <Col>
                    <EditorControls.ItemPicker
                      {...streetProps}
                      onChange={(e, v) => {
                        actions.onChange({
                          street: v,
                        });
                        setAddr({
                          ...data,
                          street: v,
                        });
                      }}
                      filter={streetFilter}
                      disabled={!data.locality}
                    />
                  </Col>
                  <Col lg={2}>
                    <EditorControls.ItemPicker
                      {...houseProps}
                      onChange={(e, v) => {
                        actions.onChange({
                          house: v,
                        });
                        setAddr({
                          ...data,
                          house: v,
                        });
                      }}
                      disabled={!data.street}
                      filter={{ street: data.street?.id }}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <EditorControls.StringInput {...place} />
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {keyParam && (
            <CalcArea
              keyParam={keyParam}
              setKeyParam={setKeyParam}
              paramArea={paramVent}
              setParamVent={setParamVent}
              setArea={setArea}
            />
            )}

            <Card className="border-0 mx-3">

              <Card.Title className="m-3">
                {'Загальні відомості про об\'єкт'}
              </Card.Title>

              <Card.Body className="px-3">

                <Row>

                  <Col>
                    <Card className="border-0 p-3 my-2">
                      <Card.Title>
                        З’єднувальна димовідвідна труба
                      </Card.Title>
                      <Card.Body className="p-1">
                        <Row>
                          <EditorControls.SelectorInput {...connectTube} />
                        </Row>
                        <Row>
                          <Col>
                            <EditorControls.SelectorInput {...turnsSmokeTube} />
                          </Col>
                          <Col>
                            <EditorControls.NumberInput {...areaConnectTubeProps} />
                          </Col>
                        </Row>
                        <Row>
                          <EditorControls.NumberInput {...lengthSmokeTubeProps} />
                        </Row>
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col>
                    <Card className="border-0 p-3 my-2">
                      <Card.Title>
                        Вентиляційні канали
                      </Card.Title>
                      <Card.Body className="p-1">
                        <Row>
                          <EditorControls.SelectorInput {...ventChannel} />
                          <EditorControls.StringInput
                            {...areaVent1}
                            append={(
                              <Button
                                variant="falcon-info"
                                size="sm"
                                onClick={() => setKeyParam('area_vent1')}
                              >
                                <FontAwesomeIcon
                                  icon={faCalculator}
                                />
                              </Button>
                                  )}
                          />
                          <EditorControls.StringInput
                            {...areaVent2}
                            append={(
                              <Button
                                variant="falcon-info"
                                size="sm"
                                onClick={() => setKeyParam('area_vent2')}
                              >
                                <FontAwesomeIcon
                                  icon={faCalculator}
                                />
                              </Button>
                                  )}
                          />
                        </Row>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col>
                    <Card className="border-0 p-3 my-2">
                      <Card.Title>
                        Димові канали
                      </Card.Title>
                      <Card.Body className="p-1">
                        <Row>
                          <EditorControls.SelectorInput {...smokeChannel} />
                          <EditorControls.StringInput
                            {...areaSmoke1}
                            append={(
                              <Button
                                variant="falcon-info"
                                size="sm"
                                onClick={() => setKeyParam('area_smoke1')}
                              >
                                <FontAwesomeIcon
                                  icon={faCalculator}
                                />
                              </Button>
                                  )}
                          />
                          <EditorControls.StringInput
                            {...areaSmoke2}
                            append={(
                              <Button
                                variant="falcon-info"
                                size="sm"
                                onClick={() => setKeyParam('area_smoke2')}
                              >
                                <FontAwesomeIcon
                                  icon={faCalculator}
                                />
                              </Button>
                                  )}
                          />
                        </Row>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                <Row>
                  <Col lg={8} xl={8}>
                    <Card className="border-0">
                      <Card.Title className="m-2">
                        Газоспоживаюче обладнання
                      </Card.Title>
                      <Card.Body>
                        <Row className="mx-1">
                          {fields && fields.objexamequpmanetrow_set && (
                          <Equpmanet
                            data={data}
                            fields={fields}
                            errors={fieldErrors}
                            onChange={actions.onChange}
                            readOnly={!permissions.canChange}
                            tableName="objexamequpmanetrow_set"
                            onDraft={actions.onDraft}
                          />
                          )}
                        </Row>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col lg={4} xl={4}>
                    <Card className="border-0 p-3">
                      <Card.Title>
                        Додаткові відомості
                      </Card.Title>
                      <Card.Body className="p-1">
                        <EditorControls.TextInput {...informationProps} rows={4} />
                      </Card.Body>
                    </Card>

                  </Col>
                </Row>
                <Row className="me-3">
                  <Card className="border-0 p-3 m-3">
                    <Card.Title>
                      Примітка
                    </Card.Title>
                    <Card.Body className="p-1">
                      <Row>
                        <EditorControls.TextInput {...noteProps} rows={3} />
                      </Row>
                    </Card.Body>
                  </Card>

                </Row>
              </Card.Body>
            </Card>

          </Container>
        </HidableContainer>
      </DocumentBody>
      <Footer
        created={data.created_at}
        updated={data.updated_at}
        readOnly={!permissions.canChange}
        inspection={data.inspection}
        authority={data.authority}
        author={data.author}
        onChange={actions.onChange}
        id={id}
        headerProps={headerProps}
      />
    </EditorContainer>
  );
}

Editor.propTypes = {
  id: PropTypes.string.isRequired,
  onSave: PropTypes.func,
  onClose: PropTypes.func,
};

Editor.defaultProps = {
  onClose: null,
  onSave: null,
};

export default Editor;
